// Loads the Square Web Payments SDK ourselves instead of letting
// react-square-web-payments-sdk do it.
//
// The card fields and the Pay button come from square.js, a script the SDK
// fetches from web.squarecdn.com at runtime. When that fetch fails, the
// library's <PaymentForm> renders `null` — not an error, not a spinner,
// nothing — so the buyer sees the order summary, the Back button, and a blank
// space where the card form should be. There is no .catch() on the load
// either, so the failure never surfaces anywhere the buyer or we can see it.
//
// It also caches its load promise in a module-level variable that it never
// clears, so once a load has failed every later attempt replays the same
// rejection: leaving the page and coming back cannot recover, only a full
// reload can.
//
// Loading it here first fixes both. We get a real error with a reason we can
// report, a timeout instead of an indefinite hang, and a retry that actually
// re-requests. <PaymentForm> is then only mounted once window.Square exists,
// where the library's own loader short-circuits and never caches anything.

const SDK_VERSION = 'v1';

// A blocked request fails fast, but a request that simply hangs — captive
// portal, dead Wi-Fi association, throttled connection — never fires an error
// at all. Without a cap of our own that is an infinite spinner.
export const SQUARE_LOAD_TIMEOUT_MS = 15_000;

// The same URLs the SDK derives from the application ID prefix, so we share
// its <script> and its window.Square fast path rather than racing a second
// copy of the SDK.
export function squareScriptUrl(applicationId) {
  if (typeof applicationId !== 'string') return null;
  if (applicationId.startsWith('sq0idp-')) return `https://web.squarecdn.com/${SDK_VERSION}/square.js`;
  if (applicationId.startsWith('sandbox-sq0idb-')) return `https://sandbox.web.squarecdn.com/${SDK_VERSION}/square.js`;
  return null;
}

function loadFailure(reason, message) {
  const error = new Error(message);
  error.reason = reason;
  return error;
}

// In-flight load, shared so concurrent callers don't request two copies.
// Cleared on rejection — that clearing is the whole difference between this
// and the SDK's cache.
let pending = null;

/**
 * Resolves with window.Square, or rejects with an Error carrying a `reason`:
 *   bad-app-id — the application ID isn't a shape square.js is served for
 *   blocked    — the request failed outright (content blocker, DNS, offline)
 *   no-global  — something answered, but it wasn't square.js (captive portal)
 *   timeout    — nothing answered in time
 */
export function loadSquareSdk(applicationId, { timeoutMs = SQUARE_LOAD_TIMEOUT_MS } = {}) {
  if (typeof window === 'undefined') {
    return Promise.reject(loadFailure('no-window', 'The payment SDK can only load in a browser.'));
  }
  if (window.Square) return Promise.resolve(window.Square);
  if (pending) return pending;

  const url = squareScriptUrl(applicationId);
  if (!url) {
    return Promise.reject(loadFailure('bad-app-id', 'The Square application ID is not in a recognized format.'));
  }

  pending = new Promise((resolve, reject) => {
    // A timed-out attempt leaves its <script> behind (see below). Drop it, or
    // the browser keeps the dead element and the retry requests nothing.
    document.querySelector(`script[src="${url}"]`)?.remove();

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    let settled = false;
    const settle = (finish, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      finish(value);
    };

    const onLoad = () => {
      // A 200 is not proof we got square.js. A captive portal or a filtering
      // proxy answering with its own HTML "loads" fine and defines nothing.
      if (window.Square) {
        settle(resolve, window.Square);
        return;
      }
      script.remove();
      settle(reject, loadFailure('no-global', 'square.js loaded but did not define window.Square.'));
    };

    const onError = () => {
      // Definitively dead — remove it so a retry issues a fresh request.
      script.remove();
      settle(reject, loadFailure('blocked', 'The browser could not load square.js from web.squarecdn.com.'));
    };

    // Left in place deliberately: a slow load may still land, and if it does
    // the window.Square check above short-circuits the retry immediately.
    const timer = setTimeout(
      () => settle(reject, loadFailure('timeout', `square.js did not load within ${Math.round(timeoutMs / 1000)}s.`)),
      timeoutMs,
    );

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  });

  pending.catch(() => { pending = null; });
  return pending;
}
